import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useMemo } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { Copy } from "lucide-react";
import PageContainer from "../../common_components/PageContainer";
import { TextInput } from "../../common_components/FormInputs";
import AppButton from "../../common_components/AppButton";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import { toast } from "../../common_components/Toast/ToastUtils";
import LinkBuilderToolHeader from "./Component/LinkBuilderToolHeader";
const INITIAL_FORM = {
    destination_url: "",
    program_offer_id: "",
    publisher_partner: "",
    placement_type: "",
    promotion_campaign_name: "",
};
const buildAffiliateLink = (form) => {
    const base = form.destination_url.trim();
    if (!base)
        return "";
    const params = {
        utm_source: form.publisher_partner.trim(),
        utm_medium: form.placement_type.trim(),
        utm_campaign: form.promotion_campaign_name.trim(),
        utm_id: form.program_offer_id.trim(),
    };
    const query = Object.entries(params)
        .filter(([, v]) => v)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v).replace(/%20/g, "+")}`)
        .join("&");
    if (!query)
        return base;
    return base.includes("?") ? `${base}&${query}` : `${base}?${query}`;
};
const LinkBuilderToolMain = () => {
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [generated, setGenerated] = useState("");
    const handleChange = useCallback((field) => (e) => {
        const value = e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
    }, []);
    const validate = useCallback((state) => {
        const next = {};
        const url = state.destination_url.trim();
        if (!url) {
            next.destination_url = "Destination URL is required";
        }
        else if (/\s/.test(url)) {
            next.destination_url = "URL cannot contain spaces";
        }
        else if (!/^https?:\/\//i.test(url)) {
            next.destination_url = "URL must start with http:// or https://";
        }
        else {
            try {
                const parsed = new URL(url);
                const host = parsed.hostname;
                // Require a hostname with a real TLD (e.g. example.com, example.co.uk)
                if (!host.includes(".") || !/\.[a-z]{2,}$/i.test(host)) {
                    next.destination_url = "Enter a valid URL with a domain (e.g. https://example.com)";
                }
            }
            catch {
                next.destination_url = "Enter a valid URL";
            }
        }
        const checkNoSpaces = (field, label, required) => {
            const raw = state[field];
            const trimmed = raw.trim();
            if (!trimmed) {
                if (required)
                    next[field] = `${label} is required`;
                return;
            }
            if (/\s/.test(trimmed))
                next[field] = `${label} cannot contain spaces`;
        };
        checkNoSpaces("publisher_partner", "Publisher / Partner", true);
        checkNoSpaces("placement_type", "Placement Type", true);
        checkNoSpaces("promotion_campaign_name", "Promotion / Campaign Name", true);
        checkNoSpaces("program_offer_id", "Program / Offer ID", false);
        return next;
    }, []);
    const handleGenerate = useCallback(() => {
        const v = validate(form);
        if (Object.keys(v).length > 0) {
            setErrors(v);
            setGenerated("");
            return;
        }
        setErrors({});
        setGenerated(buildAffiliateLink(form));
    }, [form, validate]);
    const handleCopy = useCallback(async () => {
        if (!generated)
            return;
        try {
            await navigator.clipboard.writeText(generated);
            toast.success("Affiliate link copied to clipboard");
        }
        catch {
            toast.error("Failed to copy link");
        }
    }, [generated]);
    const canGenerate = useMemo(() => Boolean(form.destination_url.trim()), [form.destination_url]);
    return (_jsxs(PageContainer, { children: [_jsx(LinkBuilderToolHeader, {}), _jsxs(Box, { className: "link-builder-tool-card", children: [_jsxs(Box, { className: "link-builder-tool-header-section", children: [_jsx(Typography, { className: "link-builder-tool-title", children: "Enter The Destination URL and Affiliate Tracking Details" }), _jsx(Typography, { className: "link-builder-tool-subtitle", children: "Fill out all fields marked with an asterisk (*), and we'll generate a trackable affiliate link for the specific partner + placement." })] }), _jsxs(Box, { className: "link-builder-tool-form-grid", children: [_jsx(TextInput, { label: "Destination URL", required: true, placeholder: "https://example.com", value: form.destination_url, onChange: handleChange("destination_url"), error: !!errors.destination_url, errorMessage: errors.destination_url, fullWidth: true }), _jsx(TextInput, { label: "Program / Offer ID (optional)", placeholder: "OFFER2026_05", value: form.program_offer_id, onChange: handleChange("program_offer_id"), error: !!errors.program_offer_id, errorMessage: errors.program_offer_id, fullWidth: true }), _jsx(TextInput, { label: "Publisher / Partner", required: true, placeholder: "wirecutter", value: form.publisher_partner, onChange: handleChange("publisher_partner"), error: !!errors.publisher_partner, errorMessage: errors.publisher_partner, fullWidth: true }), _jsx(TextInput, { label: "Placement Type", required: true, placeholder: "content", value: form.placement_type, onChange: handleChange("placement_type"), error: !!errors.placement_type, errorMessage: errors.placement_type, fullWidth: true }), _jsx(TextInput, { label: "Promotion / Campaign Name", required: true, placeholder: "new_customer_offer", value: form.promotion_campaign_name, onChange: handleChange("promotion_campaign_name"), error: !!errors.promotion_campaign_name, errorMessage: errors.promotion_campaign_name, fullWidth: true })] }), _jsx(Box, { className: "link-builder-tool-action-row", children: _jsx(AppButton, { variant: "contained", color: "primary", size: "small", className: "link-builder-tool-generate-button", onClick: handleGenerate, disabled: !canGenerate, children: "Generate Affiliate Link" }) }), generated && (_jsxs(Box, { className: "link-builder-tool-output-section", children: [_jsx(Typography, { className: "link-builder-tool-output-label", children: "Generated Affiliate Link" }), _jsxs(Box, { className: "link-builder-tool-output-row", children: [_jsx(Box, { className: "link-builder-tool-output-input-wrapper", children: _jsx(TextInput, { value: generated, readOnly: true, fullWidth: true }) }), _jsx(HoverTooltip, { title: "Copy", children: _jsx(IconButton, { className: "link-builder-tool-copy-button", onClick: handleCopy, children: _jsx(Copy, { size: 18 }) }) })] })] }))] })] }));
};
export default LinkBuilderToolMain;
